import React, { useState, useEffect, useCallback, useContext } from 'react';
import Recinto from './Recinto';
import Eventos from './Eventos';
import { AuthContext } from '../contexts/AuthContext';

interface Client 
{
  id: number;
  name: string;
  login: string;
  password: string;
}

const Recintos: React.FC = () => 
{
  const { token } = useContext(AuthContext);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [viewingEvents, setViewingEvents] = useState<number | null>(null);

  //-----------------------------------------------------------

  const fetchClients = useCallback(async () => 
  {
    setLoading(true);
    try 
    {
      const api_url = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${api_url}web/get_clients`, {
        method: 'GET',
        headers: {
          'X-AUTH-TOKEN': token || '',
        },
      });

      if (response.ok) 
      {
        const data = await response.json();
        setClients(data);
      } 
      else 
      {
        throw new Error('Error al cargar clientes.');
      }
    } 
    catch (error) 
    {
      const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
      setError(errorMsg);
    } 
    finally 
    {
      setLoading(false);
    }
  }, [token]);

  //-----------------------------------------------------------

  useEffect(() => 
  {
    fetchClients();
  }, [fetchClients]);

  //-----------------------------------------------------------

  const handleCreateClick = () => 
  {
    setSelectedClient({
      id: -1, // Para indicar que es un nuevo cliente
      name: '',
      login: '',
      password: '',
    });
  };

  //-----------------------------------------------------------

  const handleEditClick = (client: Client) => 
  {
    setSelectedClient(client);
  };

  //-----------------------------------------------------------

  const handleFormClose = () => 
  {
    setSelectedClient(null);
    fetchClients();
  };

  //-----------------------------------------------------------

  const handleEventsClick = (clientId: number) => 
  {
    setViewingEvents(clientId);
  };

  //-----------------------------------------------------------

  const handleBackFromEvents = () => 
  {
    setViewingEvents(null);
  };

  //-----------------------------------------------------------

  const findClientNameById = (id: number) => 
  {
    const client = clients.find(client => client.id === id);
    return client ? client.name : '';
  };

  //-----------------------------------------------------------

  if (viewingEvents !== null) 
  {
    const clientName = findClientNameById(viewingEvents);
    return (
      <Eventos
        clientId={viewingEvents}
        title={clientName}
        onBack={handleBackFromEvents}
      />
    );
  }

  //-----------------------------------------------------------

  return (
    <div className="eventos-container">
      {selectedClient === null ? (
        <div>
          <div>
            <table>
              <tbody>
                <tr>
                  <td>
                    <button
                      className="create-event-button"
                      onClick={handleCreateClick}
                    >
                      <i className="fas fa-plus-circle"></i> Crear RECINTO
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {loading && <p>Buscando clientes...</p>}
          {error && <p className="error-message">{error}</p>}
          {!loading && !error && clients.length === 0 && (
            <p>No hay clientes para mostrar.</p>
          )}
          {!loading && !error && clients.length > 0 && (
            <table className="eventos-table">
              <tbody>
                {clients.map(client => (
                  <tr key={client.id}>
                    <td className="evento-name">{client.name}</td>
                    <td className="evento-actions">
                      <button className="action-button edit-button" onClick={() => handleEditClick(client)}>
                        <i className="fas fa-pencil"></i>Editar
                      </button>
                    </td>
                    <td className="evento-actions">
                      <button className="action-button-wide" onClick={() => handleEventsClick(client.id)}>
                      <i className="fas fa-champagne-glasses"></i>Eventos
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div className="form-overlay">
          <Recinto
            client={selectedClient}
            onSave={handleFormClose}
            onCancel={handleFormClose}
            onDelete={(clientId) => {
              setClients(clients.filter(client => client.id !== clientId));
              handleFormClose();
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Recintos;
